/** Course validation, cloning, persistence, and save-name policy. No UI dependencies. */
import { COURSE_TILES_H, COURSE_TILES_W } from '../engine/constants'
import { OBJECT_BY_ID } from '../game/objects'
import {
  Terrain,
  type LevelData,
  type ObjectPlacement,
  type ThemeId,
} from '../game/types'
import { COURSES } from './courses'

/**
 * Longest course name the editor will hold. The inline rename field stops here
 * and `resolveSaveName` trims its numeric suffix back inside the same budget,
 * so a deduplicated copy is never longer on screen than a typed one.
 */
export const MAX_NAME = 24

/** Name a brand new, untouched course gets. */
export const NEW_COURSE_NAME = 'My Course'

export const THEMES: ThemeId[] = [
  'overworld',
  'underground',
  'night',
  'castle',
  'sky',
]

// ── persistence ────────────────────────────────────────────────────────────

/** `localStorage` key holding every saved course, keyed by level name. */
export const STORAGE_KEY = 'tavi:courses'

/** Used when `localStorage` is unavailable (private mode, SSR, tests). */
const memoryCourses: Record<string, LevelData> = Object.create(null)
/** Failed writes take precedence over older durable copies until recovery. */
const pendingCourses = new Set<string>()
let storageWarned = false

function warnStorage(): void {
  if (storageWarned) return
  storageWarned = true
  console.warn('[editor] localStorage unavailable; courses live in memory only')
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    warnStorage()
    return null
  }
}

function clampInt(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) value = fallback
  const number = value as number
  return Math.max(min, Math.min(max, Math.round(number)))
}

/**
 * A true deep copy. Hand-rolled rather than `JSON.parse(JSON.stringify(...))`
 * so the result stays typed and a hostile `toJSON` cannot smuggle a function
 * into the level.
 */
export function cloneLevelData(data: LevelData): LevelData {
  const out: LevelData = {
    name: data.name,
    width: data.width,
    height: data.height,
    theme: data.theme,
    time: data.time,
    startX: data.startX,
    startY: data.startY,
    tiles: data.tiles.slice(),
    objects: data.objects.map((o) => {
      const copy: ObjectPlacement = { id: o.id, tx: o.tx, ty: o.ty }
      if (o.props !== undefined) copy.props = { ...o.props }
      return copy
    }),
  }
  if (data.author !== undefined) out.author = data.author
  return out
}

/**
 * Repair anything loaded from storage: wrong types, short tile arrays,
 * placements for objects that no longer exist, out-of-range start. Returns
 * null only when the value is not level-shaped at all.
 */
export function normalizeLevelData(
  value: unknown,
  fallbackName = 'Untitled',
): LevelData | null {
  if (value === null || typeof value !== 'object') return null
  // An array is not a level-shaped value, however many of its entries happen to
  // be numbers. Accepting one would turn `[1,2,3]` into a blank 160x30 course
  // under the storage key's name, which is a phantom in the course select that
  // nobody can edit or delete.
  if (Array.isArray(value)) return null
  const raw = value as Record<string, unknown>

  const name =
    typeof raw.name === 'string' && raw.name.trim().length > 0
      ? raw.name
      : fallbackName
  const width = clampInt(raw.width, 1, 512, COURSE_TILES_W)
  const height = clampInt(raw.height, 1, 256, COURSE_TILES_H)

  const tiles: number[] = new Array(width * height).fill(Terrain.EMPTY)
  if (Array.isArray(raw.tiles)) {
    for (let i = 0; i < tiles.length; i++) {
      const t: unknown = raw.tiles[i]
      tiles[i] =
        typeof t === 'number' && t >= 0 && t <= Terrain.PLATFORM
          ? Math.round(t)
          : Terrain.EMPTY
    }
  }

  const objects: ObjectPlacement[] = []
  if (Array.isArray(raw.objects)) {
    for (const entry of raw.objects) {
      if (entry === null || typeof entry !== 'object') continue
      const p = entry as Record<string, unknown>
      // An id the registry does not know is dropped, exactly like every other
      // malformed field: there is no compatibility table for retired spellings.
      if (typeof p.id !== 'string' || !OBJECT_BY_ID.has(p.id)) continue
      const placement: ObjectPlacement = {
        id: p.id,
        tx: clampInt(p.tx, 0, width - 1, 0),
        ty: clampInt(p.ty, 0, height - 1, 0),
      }
      if (
        p.props !== null &&
        typeof p.props === 'object' &&
        !Array.isArray(p.props)
      ) {
        // Props are scalars by contract. Reject nested/shared data and values
        // that JSON would drop or turn into null on the next save.
        placement.props = Object.fromEntries(
          Object.entries(p.props).filter(
            ([, prop]) =>
              typeof prop === 'string' ||
              typeof prop === 'boolean' ||
              (typeof prop === 'number' && Number.isFinite(prop)),
          ),
        )
      }
      objects.push(placement)
    }
  }

  const theme = THEMES.includes(raw.theme as ThemeId)
    ? (raw.theme as ThemeId)
    : 'overworld'
  const out: LevelData = {
    name,
    width,
    height,
    theme,
    time: clampInt(raw.time, 0, 9999, 300),
    startX: clampInt(raw.startX, 0, width - 1, 1),
    startY: clampInt(raw.startY, 0, height - 1, height - 2),
    tiles,
    objects,
  }
  if (typeof raw.author === 'string') out.author = raw.author
  return out
}

/**
 * Read and normalise one stored course map key. `null` means the key could not
 * be read at all (denied storage); absent or malformed content is an empty map.
 */
function readCourseMap(
  s: Storage,
  key: string,
): Record<string, LevelData> | null {
  let raw: string | null
  try {
    raw = s.getItem(key)
  } catch {
    warnStorage()
    return null
  }
  try {
    if (raw === null) return {}
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
      return {}
    const out: Record<string, LevelData> = Object.create(null)
    for (const [name, value] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      const level = normalizeLevelData(value, name)
      if (level !== null) out[level.name] = level
    }
    return out
  } catch (e) {
    console.warn('[editor] could not read saved courses', e)
    return {}
  }
}

/** Merge readable storage with cached copies, retaining pending edits. */
function mergeCourseMap(
  saved: Record<string, LevelData> | null,
): Record<string, LevelData> {
  const out: Record<string, LevelData> = Object.create(null)
  for (const [name, level] of Object.entries(memoryCourses)) {
    out[name] = cloneLevelData(level)
  }
  if (saved !== null) {
    for (const [name, level] of Object.entries(saved)) {
      if (!pendingCourses.has(name)) out[name] = level
    }
  }
  return out
}

/** Every saved course, merged over the in-memory fallback. */
export function loadSavedCourses(): Record<string, LevelData> {
  const s = storage()
  return mergeCourseMap(s === null ? null : readCourseMap(s, STORAGE_KEY))
}

/**
 * Merge a course into the saved set, keyed by level name. Returns false when
 * it could not be written to `localStorage` (it is still kept in memory).
 */
export function saveCourse(data: LevelData): boolean {
  const level = normalizeLevelData(data)
  if (level === null) return false
  memoryCourses[level.name] = cloneLevelData(level)
  pendingCourses.add(level.name)
  const s = storage()
  if (s === null) return false
  const saved = readCourseMap(s, STORAGE_KEY)
  // A denied read is not an empty library: writing now could erase courses
  // that this session has never seen. Keep the edit pending until recovery.
  if (saved === null) return false
  const merged = mergeCourseMap(saved)
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(merged))
    pendingCourses.clear()
    return true
  } catch {
    warnStorage()
    return false
  }
}

// ── naming policy ──────────────────────────────────────────────────────────

/**
 * What the editor was opened on, which is what decides where a save may land.
 *
 * `builtIn` is the half that matters. The five courses in `COURSES` are the
 * shipped originals: `docs/LEVELS.md` documents them tile by tile and the
 * breadth-first reachability pass described there is measured against those
 * exact grids. The runtime is already one way in without meaning to — a brick
 * head-bump calls `Level.removeObject`, which splices the placement list in
 * place, which is why `App.loadLevel` clones — and the editor must not become a
 * second. So a built-in is read-only here: the editor works on a private copy,
 * and the save lands *beside* them under a fresh name.
 */
export interface CourseSource {
  /** The name the course had in the library when this session opened. */
  name: string
  /** True when it came from `COURSES`, i.e. it may not be overwritten. */
  builtIn: boolean
}

/** Names compare case-insensitively: `green hills` is not a new course. */
function nameKey(name: string): string {
  return name.trim().toLowerCase()
}

/** Every name already in use, built-ins included. */
function takenNames(): Set<string> {
  const out = new Set<string>()
  for (const name of Object.keys(COURSES)) out.add(nameKey(name))
  for (const name of Object.keys(loadSavedCourses())) out.add(nameKey(name))
  return out
}

/**
 * The name a course will be stored under.
 *
 * Names are unique across the whole library, because the selector is a list of
 * names and two rows reading the same are a trap for the player. So:
 *
 *  - **saving over yourself is allowed**: a user course saved under the name it
 *    was opened with replaces that course, which is what "edit my level and
 *    save again" means;
 *  - **anything else that is taken gets the lowest free numeric suffix**, so
 *    editing _Green Hills_ lands as _Green Hills 2_, and a built-in can never
 *    be overwritten however the rename went.
 *
 * Deterministic: the same inputs always give the same answer, with no clock and
 * no randomness, so the tests can assert an exact name and a player gets the
 * same course twice.
 */
export function resolveSaveName(
  desired: string,
  source: CourseSource | null,
): string {
  const trimmed = desired.trim()
  const wanted = trimmed.length > 0 ? trimmed : NEW_COURSE_NAME
  if (
    source !== null &&
    !source.builtIn &&
    nameKey(source.name) === nameKey(wanted)
  ) {
    // Keep the library's own spelling, so a retyped capitalisation does not
    // fork the course into a near-duplicate.
    return source.name
  }
  const taken = takenNames()
  if (!taken.has(nameKey(wanted))) return wanted
  // The taken set is finite; distinct numeric suffixes always reach a free name.
  for (let n = 2; ; n++) {
    const suffix = ` ${n}`
    // Trim the stem rather than letting the suffix push the name past the
    // budget the rename field enforces. The loop re-checks for collisions, so
    // a shared stem just walks on to the next number.
    const stem = wanted.slice(0, Math.max(1, MAX_NAME - suffix.length))
    const candidate = `${stem}${suffix}`
    if (!taken.has(nameKey(candidate))) return candidate
  }
}
